/**
 * 本机能力接缝的类型。
 *
 * # 这一层存在的理由
 *
 * CLAUDE.md §4.2 第一条跨平台约束：**前端不碰任何 Node / 系统 API**，
 * 需要本机能力一律走这里，Web 端给降级实现。
 *
 * 一个组件只要直接摸了本机 API，它就只在一种壳里能跑，而这个产品的前提是
 * Web 与 macOS **共用同一份产物**（`tauri.conf.json` 的 `frontendDist`
 * 指的就是 `apps/console/dist`）。那种耦合不会报错，只会在另一种壳里
 * 静默少一块功能。
 *
 * # 为什么失败是返回值，不是异常
 *
 * 这一层的每个方法都**不抛**。抛出去的话，调用方要么写 try/catch，
 * 要么忘记写——而这个仓库已经为「忘记写」付过一次账：
 * `screens/remote-ai-join/index.tsx` 那句没有 catch 的 `writeText(...).then(...)`
 * 在剪贴板不可用时**一个字都不说**，而那一屏的 API key 只显示一次。
 *
 * 失败做成返回值之后，「没处理失败」是一个 `ok` 没被读过的值，
 * 而不是一条谁都看不见的 rejected promise。
 */

/**
 * 这份产物此刻跑在哪个壳里。
 *
 * 加一档（Capacitor 那份将来是 `'ios'` / `'android'`）时，`index.ts` 里的
 * 工厂表会**当场编不过**——那张表是按这个联合类型建的映射类型，
 * 少一个键就是一个类型错误。这不是注释，是编译器的事。
 */
export type Surface = 'web' | 'macos'

/**
 * 一次本机调用的结果。**永远不抛，失败是一个值。**
 *
 * 三档失败是刻意分开的，判据是 CLAUDE.md §8
 * 「两件事如果要求用户做的下一个动作不同，就不许合成一种说法」：
 *
 * | reason | 这个壳里发生了什么 | 用户的下一个动作 |
 * |---|---|---|
 * | `unsupported` | 这条路在这个壳里根本不存在（没有 API、非安全上下文） | 手动选中文本，**别再点了** |
 * | `denied` | 有这条路，被权限挡住了 | 去允许它，或者手动选中 |
 * | `failed` | 有这条路、也允许了，这一次没成 | 再点一次 |
 *
 * 合成一句「复制失败」的话，第一种情况下的用户会一直点那个永远不会成的按钮。
 */
export type Outcome =
  | { readonly ok: true }
  | { readonly ok: false; readonly reason: OutcomeFailure }

export type OutcomeFailure = 'unsupported' | 'denied' | 'failed'

export const ok: Outcome = { ok: true }
export const unsupported: Outcome = { ok: false, reason: 'unsupported' }
export const denied: Outcome = { ok: false, reason: 'denied' }
export const failed: Outcome = { ok: false, reason: 'failed' }

/**
 * 系统剪贴板。
 *
 * 消费方（这一轮接上的八处）：`remote-ai-join`、`project-import/detail`、
 * `activity/view`、`bridge/index` ×2、`bridge/tokens`、`development-task` ×3。
 */
export interface ClipboardCapability {
  /**
   * 把一段文本放进系统剪贴板。
   *
   * **不抛。** 剪贴板不可用、被拒、或者这一次没成，都回一个 `ok: false`。
   */
  copyText(text: string): Promise<Outcome>
}

/**
 * 这个壳能做的事。
 *
 * # 加一个能力之前先数一遍谁要用
 *
 * 这个接口刻意很小。CLAUDE.md 的判据是
 * 「**一个共享包如果没有任何消费方引用它，它就不是「抽出来了」，是「多了一份」**」，
 * 而它对包里的每一个成员同样成立。
 *
 * 数过之后**没有**放进来的（2026-09-02，`apps/console/src` 全量扫描）：
 *
 * | 能力 | 原生那侧 | 消费方 | 结论 |
 * |---|---|---|---|
 * | 钥匙串 | `keychain_{available,get,set,remove}` 四条命令都在 | **0** | 不建。建了只是把孤儿从 Rust 挪到 TS |
 * | 通知 | `notification:default` 插件已注册 | **0**（`activity/view.tsx` 明写「系统通知 · P1 —— 站内动态闭环之后再做」） | 不建，等那条闭环 |
 * | 生物识别 | 无 | **0**（Passkey 已随用户链路退役，`screens/auth/form.tsx`、`account/index.tsx`；全仓零 WebAuthn 调用） | 不建 |
 * | 选文件夹 | `folder_*` + `plugin:dialog\|open` | 1（`screens/lulu/folder-adapter.ts`） | 该收进来，但 lulu 屏这一轮归别人 |
 * | 录音 | 无（浏览器 API） | 2（`screens/memo/capture.tsx`、`screens/lulu/voice-input.tsx`，两份近乎重复的实现） | 该收进来，同上 |
 *
 * 前三行是「没有消费方」，后两行是「有消费方但不在这一轮的写域里」——
 * 两种都不该现在就摆一个接口出来占位。
 */
export interface PlatformCapabilities {
  readonly surface: Surface
  readonly clipboard: ClipboardCapability
}

/**
 * 这一层认得的宿主对象。
 *
 * 刻意不写成 `Window`：这份代码在 Node（vitest 的非 jsdom 环境、将来的 SSR）
 * 里也要能被 import 而不炸，所以判据只能是「这个对象上有没有那个键」。
 */
export type HostLike = Record<string, unknown>
